import {
  Box,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead
} from '@mui/material';
import { StyledTableCell, StyledTableRow } from '@components/StyledTable';
import { ArrElement } from '@utils/types';
import {
  FetchMainBizQuery,
  MainBizSearchType,
  Maybe,
  useFetchMainBizQuery
} from '@generated/graphql';
import useQueryString from '@hooks/useQueryString';
import { useState } from 'react';
import { DetailDrawer } from '@components/DetailDrawer';

export type MainBizData = ArrElement<
  NonNullable<FetchMainBizQuery['fetchMainBiz']['data']>['icxModelList']
>;

type MainBizParamsType = {
  page: string;
  q: string;
  searchType: Maybe<MainBizSearchType>;
};

const LIMIT = 14;

const MainBiz = () => {
  const { onUpdateQueryString, query } = useQueryString<MainBizParamsType>();

  const [drawer, setDrawer] = useState<{
    open: boolean;
    data?: MainBizData;
  }>({
    open: false
  });
  const page = Number(query.page ?? 1);

  const { data, loading, error } = useFetchMainBizQuery({
    variables: {
      input: {
        keyword: query.q,
        limit: LIMIT,
        offset: (page - 1) * LIMIT,
        mainBizSearchType: query.searchType
      }
    }
  });

  if (loading || error) {
    return null;
  }

  const handleOpenDetailPage = (
    id: string | null | undefined,
    data: MainBizData
  ) => {
    if (!id) {
      return;
    }

    setDrawer({ open: true, data });
  };

  const mainBizList = data?.fetchMainBiz.data?.icxModelList ?? [];
  const mainBizTotalCount = data?.fetchMainBiz.data?.count ?? 0;

  return (
    <>
      <TableContainer component={Paper} sx={{ minWidth: 800 }}>
        <Table>
          <TableHead>
            <StyledTableRow>
              <StyledTableCell>사업자명</StyledTableCell>
              <StyledTableCell>대표자명</StyledTableCell>
              <StyledTableCell>업종코드</StyledTableCell>
              <StyledTableCell>업종명</StyledTableCell>
              <StyledTableCell>지역</StyledTableCell>
              <StyledTableCell>소재지</StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {mainBizList.map((mainBizData) => (
              <StyledTableRow
                onClick={() =>
                  handleOpenDetailPage(mainBizData.id, mainBizData)
                }
                key={mainBizData.id}
                sx={{
                  '&:hover': {
                    cursor: 'pointer',
                    backgroundColor: '#00000030'
                  }
                }}
              >
                <StyledTableCell>{mainBizData.companyName}</StyledTableCell>
                <StyledTableCell>
                  {mainBizData.representativeName}
                </StyledTableCell>
                <StyledTableCell>{mainBizData.industryCode}</StyledTableCell>
                <StyledTableCell>{mainBizData.industryName}</StyledTableCell>
                <StyledTableCell>{mainBizData.region}</StyledTableCell>
                <StyledTableCell>{mainBizData.location}</StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        padding={2}
        width={'100%'}
        display={'flex'}
        justifyContent={'center'}
      >
        <Pagination
          count={Math.ceil(mainBizTotalCount / LIMIT)}
          page={page}
          color={'primary'}
          onChange={(e, value) => {
            onUpdateQueryString({ page: `${value}` });
          }}
        />
        <DetailDrawer
          open={drawer.open}
          onClose={() => setDrawer({ open: false })}
          detailInfo={drawer.data}
          tables={[
            {
              title: '기본 정보',
              table: DefaultTable
            }
          ]}
        />
      </Box>
    </>
  );
};

export default MainBiz;

const DefaultTable = {
  companyName: '사업자명',
  representativeName: '대표자명',
  industryCode: '업종코드',
  industryName: '업종명',
  region: '지역',
  location: '소재지',
  certificationExpirationDate: '인증만료'
};
